import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Card, Typography } from "@mui/material";
import { useIntl } from "react-intl";
import { DEFAULT_GRADIENT, surveyBackgroundStyle } from "./surveyStyle";

// Seconds the confirmation shows before the survey's redirect link opens.
export const REDIRECT_COUNTDOWN_SECONDS = 5;

// The confirmation page a respondent sees after submitting — the one the builder's
// submission settings customise (success title/description, end message, colours,
// logo, redirect link). Traditional and Modern surveys both end here.
//
// `redirect` false (the builder's preview) shows the page without following the
// link. `footer` is rendered under the card's text (the preview's Restart).
export default function SurveyCompletion({ settings = {}, redirect = true, footer = null }) {
    const intl = useIntl();
    const [redirectCountdown, setRedirectCountdown] = useState(null);
    const [isRedirecting, setIsRedirecting] = useState(false);
    const textColor = settings.completionTextColor || "#2e7d32";
    const redirectLink = redirect ? settings.redirectLink : null;

    const doRedirect = useCallback((url) => {
        setIsRedirecting(true);
        // Small tick so React paints the blank screen before the browser navigates
        setTimeout(() => {
            window.location.href = url;
        }, 50);
    }, []);

    // Auto-redirect when a redirect link is configured
    useEffect(() => {
        if (!redirectLink) return undefined;
        setRedirectCountdown(REDIRECT_COUNTDOWN_SECONDS);
        const interval = setInterval(() => {
            setRedirectCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(interval);
                    doRedirect(redirectLink);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [redirectLink, doRedirect]);

    if (isRedirecting) {
        return <Box sx={{ minHeight: "100vh", width: "100%", backgroundColor: "#fff" }} />;
    }

    return (
        <Box
            data-testid="survey-completion"
            sx={{
                // Use completion background gradient if set, otherwise fall back to survey background
                ...(settings.completionBackgroundGradient
                    ? {
                          background: `linear-gradient(135deg, ${settings.completionBackgroundGradient.start || DEFAULT_GRADIENT.start}, ${settings.completionBackgroundGradient.end || DEFAULT_GRADIENT.end})`,
                          minHeight: "100vh",
                          width: "100%",
                      }
                    : surveyBackgroundStyle(settings)),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 4,
            }}
        >
            <Card
                sx={{
                    maxWidth: "600px",
                    width: "100%",
                    p: 4,
                    textAlign: "center",
                    borderRadius: "16px",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                    background: settings.completionCardBackgroundColor || "rgba(255, 255, 255, 0.95)",
                }}
            >
                {settings.logo && (
                    <Box sx={{ mb: 3, display: "flex", justifyContent: "center" }}>
                        <img src={settings.logo} alt="Logo" style={{ maxWidth: "150px", maxHeight: "80px", objectFit: "contain" }} />
                    </Box>
                )}
                <Typography variant="h4" sx={{ color: textColor, fontWeight: "bold", mb: 2 }}>
                    {settings.successTitle || intl.formatMessage({ id: "survey_builder.default_success_title", defaultMessage: "Thank you!" })}
                </Typography>
                <Typography variant="body1" sx={{ color: textColor, mb: 3, lineHeight: 1.6 }}>
                    {settings.successDescription ||
                        intl.formatMessage({ id: "survey_builder.default_success_description", defaultMessage: "Your response has been recorded successfully." })}
                </Typography>
                {settings.endMessage && (
                    <Typography variant="body2" sx={{ color: textColor, fontStyle: "italic", opacity: 0.8 }}>
                        {settings.endMessage}
                    </Typography>
                )}
                {redirectLink && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="body2" sx={{ color: textColor, mb: 2, opacity: 0.8 }}>
                            {redirectCountdown > 0
                                ? intl.formatMessage(
                                      { id: "survey.redirect_countdown", defaultMessage: "Redirecting in {seconds} second{plural}…" },
                                      { seconds: redirectCountdown, plural: redirectCountdown === 1 ? "" : "s" }
                                  )
                                : intl.formatMessage({ id: "survey.redirecting", defaultMessage: "Redirecting…" })}
                        </Typography>
                        <Button
                            variant="outlined"
                            onClick={() => doRedirect(redirectLink)}
                            sx={{
                                color: textColor,
                                borderColor: textColor,
                                "&:hover": { borderColor: textColor, backgroundColor: `${textColor}18` },
                            }}
                        >
                            {intl.formatMessage({ id: "survey.redirect_now", defaultMessage: "Continue" })}
                        </Button>
                    </Box>
                )}
                {footer}
            </Card>
        </Box>
    );
}
